import { XMarkIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { getFileNameUrl } from "configs/images.config";
import {
  ChangeEvent,
  FC,
  forwardRef,
  MouseEvent,
  useEffect,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { mergeRefs } from "react-merge-refs";
import { useToast } from "utils/customHooks/useToast";
import classes from "./FilePicker.module.scss";

const MAX_FILES_COUNT = 10;

export type FilePickerType = {
  file: File | string;
  onDelete: (e: MouseEvent) => void;
};

export const FilePreview: FC<FilePickerType> = ({ file, onDelete }) => {
  const fileName = file instanceof File ? file.name : file;
  let extension = fileName.split(".")[fileName.split(".").length - 1];
  if (extension.length > 5) extension = "PDF";
  const href =
    file instanceof File ? URL.createObjectURL(file) : getFileNameUrl(file);

  return (
    <a
      target="_blank"
      href={href}
      rel="noreferrer"
      download
      className={classNames(classes["file-preview"])}
    >
      <div>
        <div className={classes["file-extension"]}>{extension}</div>
        <span>{fileName}</span>
      </div>
      <button onClick={onDelete}>
        <XMarkIcon />
      </button>
    </a>
  );
};

const mimeTypes = {
  file: {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "application/msword": [".doc"],
  },
  image: {
    "image/*": [".png", ".jpeg", ".jpg"],
  },
};

type FilePickerValueType = (File | string)[] | FileList | string;

export type FilePickerProps = {
  name?: string;
  onSelect?: (files: (File | string)[]) => void;
  label: string;
  type?: "file" | "image";
  multiple?: boolean;
  showSelectedFiles?: boolean;
  value?: FilePickerValueType;
  maxFilesCount?: number;
  onChange?: (files: (File | string)[] | ChangeEvent) => void;
};
export const FilePicker = forwardRef<unknown, FilePickerProps>(
  (
    {
      onSelect,
      label,
      type = "file",
      multiple = false,
      showSelectedFiles = true,
      value,
      maxFilesCount = MAX_FILES_COUNT,
      ...props
    },
    passedRef
  ) => {
    const [selectedFiles, setFiles] = useState<(File | string)[] | FileList>(
      []
    );
    const { showToast } = useToast();

    useEffect(() => {
      if (typeof value === "string" && value.length) setFiles([value]);
      else if (value instanceof FileList) {
        setFiles(Array.from(value));
      } else setFiles((value as (File | string)[]) || []);
    }, [value]);

    const handleDrop = (newFiles: File[]) => {
      let files = multiple
        ? [...Array.from(selectedFiles), ...newFiles]
        : newFiles;
      if (files && files.length > maxFilesCount && maxFilesCount > 0) {
        files = files.slice(0, maxFilesCount);
        showToast(
          `It's not possible to select more than ${maxFilesCount} files`,
          "error"
        );
      }
      if (files && files.length) {
        onSelect && onSelect(files);
        const dT = new DataTransfer();
        for (const file of files) {
          dT.items.add(file as File);
        }
        setFiles(files);
        props.onChange &&
          // @ts-ignore
          props.onChange({ target: { value: dT.files, name: props.name } });
      }
    };

    const deleteFile = (e: MouseEvent, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      const files = [
        ...Array.from(selectedFiles).filter((_, i) => i !== index),
      ];
      setFiles(files);
      if (props.onChange) {
        props.onChange(files);
      }
    };

    const {
      getRootProps,
      getInputProps,
      isDragReject,
      isDragAccept,
      isDragActive,
    } = useDropzone({
      accept: mimeTypes[type],
      onDrop: handleDrop,
      multiple,
    });

    const getHelperText = () => {
      switch (type) {
        case "file":
          return "(Document should be: Doc, Docx, or PDF )";
        case "image":
        default:
          return "(Images should be .jpg, or .png and less than 2MB)";
      }
    };

    // @ts-ignore
    const { ref: dropInputRef, ...inputProps } = getInputProps();

    const ref = mergeRefs([passedRef, dropInputRef]);
    return (
      <div className={classes.wrapper}>
        <div
          className={classes["container"]}
          data-testid={"label"}
          {...getRootProps()}
        >
          <input
            type="file"
            multiple={multiple}
            data-testid={"input-file"}
            ref={ref}
            {...props}
            {...inputProps}
          />
          {label && <div className={classes["label-text"]}>{label}</div>}
          <div
            className={
              classes["content"] +
              " p-4 " +
              `${isDragReject ? classes["rejected"] : ""}`
            }
          >
            <div className={"icon-upload"} />

            {!isDragReject && !isDragActive && (
              <div className="capitalize">
                Drag the {type} to upload or{" "}
                <span className={"text-underline"}>Select</span>
              </div>
            )}
            {isDragReject && <div>The selected file is not acceptable</div>}
            {isDragActive && isDragAccept && <div>Drop here</div>}
            <span>{getHelperText()}</span>
          </div>
        </div>
        {showSelectedFiles && selectedFiles.length > 0 && (
          <div className="d-flex justify-content-start flex-wrap w-100 pb-2">
            {Array.from(selectedFiles).map((file, i) => (
              <FilePreview
                key={i}
                file={file}
                onDelete={(e) => deleteFile(e, i)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);
